import React from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

/**
 * One-click sandbox launcher:
 *   - Calls POST /api/public/sandbox
 *   - Stores access_token + refresh_token in localStorage
 *   - Hard-redirects to / so AuthContext re-reads the token and shows the tenant dashboard
 *
 * Props:
 *   label?         — button label (default "Launch live sandbox →")
 *   className?     — tailwind / mk-btn classes
 *   company?       — optional preset company name
 *   variant?       — 'primary' | 'secondary' | 'ghost' (maps to mk-btn-*)
 *   testId?        — data-testid
 *   children?      — override rendered content
 */
export default function LaunchSandboxButton({
  label = "Launch live sandbox →",
  className = "",
  company,
  variant = "secondary",
  testId = "mk-launch-sandbox",
  children,
}) {
  const [busy, setBusy] = React.useState(false);
  const launch = async () => {
    if (busy) return;
    setBusy(true);
    toast.loading("Spinning up your sandbox…", { id: "sandbox-launch" });
    try {
      const body = company ? { company } : {};
      const { data } = await axios.post(`${API}/public/sandbox`, body);
      if (!data?.access_token) throw new Error("No token returned");
      localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      toast.success("Sandbox ready — taking you in…", { id: "sandbox-launch" });
      // Hard-navigate so AuthProvider re-bootstraps
      window.location.href = "/";
    } catch (err) {
      setBusy(false);
      const msg = err?.response?.data?.detail || "Couldn't launch the sandbox. Please try again.";
      toast.error(msg, { id: "sandbox-launch" });
    }
  };

  const variantClass =
    variant === "primary" ? "mk-btn-primary" :
    variant === "ghost" ? "mk-btn-ghost" :
    "mk-btn-secondary";

  return (
    <button
      type="button"
      onClick={launch}
      disabled={busy}
      className={`mk-btn ${variantClass} ${busy ? "opacity-70 cursor-wait" : ""} ${className}`}
      data-testid={testId}
    >
      {busy ? (
        <span className="inline-flex items-center gap-2">
          <span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
          Launching…
        </span>
      ) : (children || label)}
    </button>
  );
}
